import { useTranslation } from 'react-i18next';
import { Lock, Mic, Paperclip } from 'lucide-react';
import { AttachmentImage } from '../ui/AttachmentImage';
import { formatTime } from '../../lib/format';
import { type ThreadAttachment, type ThreadMessage, mediaKind } from './threadModel';

export interface ChatBubbleProps {
  message: ThreadMessage;
  /** The viewer's own message: right-aligned, tinted in the header green. */
  mine: boolean;
  /** Name above the body — received bubbles in a multi-party thread, first of a run only. */
  showSender: boolean;
  /** First bubble of a run: the pointer and the squared shoulder, and a little air above. */
  tail: boolean;
}

/**
 * One message, in the shape every member of staff already knows from their own phone.
 *
 * Own messages sit right in the header green with light text; everyone else's sit left
 * on `surface-2`. That alignment is the whole orientation cue in a dim plant room, so it
 * is colour *and* side, never one alone. The time sits inside the bubble, bottom-right,
 * small and muted, through the shared formatter — the day lives in the separator above.
 *
 * Internal notes keep their padlock and a dashed edge (ticket chat only): a note the
 * contractor cannot see must not read like one they can.
 */
export function ChatBubble({ message, mine, showSender, tail }: ChatBubbleProps) {
  const internal = message.isInternal === true;

  return (
    <div
      data-mine={mine}
      data-tail={tail}
      className={`flex ${mine ? 'justify-end' : 'justify-start'} ${tail ? 'mt-2' : 'mt-0.5'}`}
    >
      <div
        className={`relative max-w-[82%] rounded-2xl px-3 pb-1.5 pt-1.5 text-sm ${
          mine ? 'bg-brookfields-green text-white' : 'bg-surface-2 text-slate-100'
        } ${tail ? (mine ? 'rounded-tr-md' : 'rounded-tl-md') : ''} ${
          internal ? 'border border-dashed border-amber-500/60' : ''
        }`}
      >
        {tail && (
          <span
            aria-hidden="true"
            className={`absolute top-0 size-2.5 bg-inherit ${
              mine
                ? '-right-1.5 [clip-path:polygon(0_0,100%_0,0_100%)]'
                : '-left-1.5 [clip-path:polygon(0_0,100%_0,100%_100%)]'
            }`}
          />
        )}

        {showSender && (
          <p className="mb-0.5 text-xs font-semibold text-brookfields-gold-light">
            {message.senderName}
          </p>
        )}

        {internal && <InternalMark />}

        {message.body && <p className="whitespace-pre-wrap break-words">{message.body}</p>}

        {message.attachments && message.attachments.length > 0 && (
          <Attachments attachments={message.attachments} />
        )}

        <p
          className={`mt-0.5 text-right text-[11px] leading-4 tabular-nums ${
            mine ? 'text-white/80' : 'text-slate-400'
          }`}
        >
          <time dateTime={message.createdAt}>{formatTime(message.createdAt)}</time>
        </p>
      </div>
    </div>
  );
}

function InternalMark() {
  const { t } = useTranslation();
  return (
    <p className="mb-0.5 flex items-center gap-1 text-[11px] text-amber-300">
      <Lock className="size-3" aria-hidden="true" />
      {t('chat.internal')}
    </p>
  );
}

/**
 * Photos as thumbnails; anything else as a labelled chip. The chat payload carries no
 * dimensions, so none are passed — a guess would shift the layout rather than hold it.
 */
function Attachments({ attachments }: { attachments: ThreadAttachment[] }) {
  const { t } = useTranslation();

  return (
    <ul className="mt-1.5 flex flex-wrap gap-1.5">
      {attachments.map((attachment) => {
        const kind = mediaKind(attachment.mimeType);
        return (
          <li key={attachment.id}>
            {kind === 'photo' ? (
              <AttachmentImage
                src={attachment.url}
                alt={t('chat.photoAlt')}
                className="size-32 rounded-lg object-cover"
              />
            ) : (
              <a
                href={attachment.url}
                className="flex min-h-[40px] items-center gap-1.5 rounded-lg bg-black/20 px-2 text-xs no-underline"
              >
                {kind === 'voice' ? (
                  <Mic className="size-4" aria-hidden="true" />
                ) : (
                  <Paperclip className="size-4" aria-hidden="true" />
                )}
                {t(kind === 'voice' ? 'chat.voiceNote' : 'chat.file')}
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
}
